/**
 * Layout Config Wizard – step-by-step setup for one orientation.
 * Responsive; works across screen sizes and aspect ratios.
 */

import {
    getConfig,
    updateConfig,
    setFreeScaleSize,
    setUseDiscreteMultipliers,
    setGameScreenScale,
    setScreenTopOffset,
    setButtonSize,
    setOffsets,
    setButtonFontSize,
    setScreenBorderRadius,
    setScreenBorderEnabled,
    fetchAndApplyMatchedConfig,
} from './LayoutConfig.js';
import { triggerResize } from '../setup.js';

const STEPS = [
    'useSuggestedConfig',
    'screenSize',
    'screenPosition',
    'roundedCorners',
    'dpadSize',
    'dpadPosition',
    'abSize',
    'abPosition',
    'abGapAlignment',
    'abAlignment',
    'abFontSize',
    'startSelectSize',
    'startSelectPosition',
    'startSelectGap',
    'startSelectFontSize',
    'final',
];

export default class LayoutConfigWizard {
    constructor(isPortrait = null) {
        this.isPortrait = isPortrait ?? window.innerHeight > window.innerWidth;
        this.stepIndex = 0;
        this.snapshot = null;
        this.overlay = null;
        this.panel = null;
    }

    enterPreview() {
        if (!this.overlay || !this.panel) return;
        this.panel.style.pointerEvents = 'none';
        this.overlay.style.opacity = '0';
        this.overlay.style.transition = 'opacity 0.15s ease';
        this.panel.style.opacity = '0';
        this.panel.style.transition = 'opacity 0.15s ease';
        const exit = () => this.exitPreview();
        this.overlay.addEventListener('click', exit, { once: true });
    }

    exitPreview() {
        if (!this.overlay || !this.panel) return;
        this.overlay.style.opacity = '';
        this.overlay.style.transition = '';
        this.panel.style.opacity = '';
        this.panel.style.transition = '';
        this.panel.style.pointerEvents = '';
    }

    async show() {
        this.stepIndex = 0;
        this.createOverlay();
        this.initialSnapshot = getConfig(this.isPortrait);
        this.saveSnapshot();
        this.renderStep();
        this.overlay.style.display = 'flex';
    }

    exitAndDiscard() {
        if (this.initialSnapshot) {
            updateConfig(this.isPortrait, this.initialSnapshot);
            triggerResize();
        }
        this.hide();
    }

    hide() {
        this.exitPreview();
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        this.overlay = null;
        this.panel = null;
        triggerResize();
    }

    createOverlay() {
        const overlay = document.createElement('div');
        overlay.className = 'layout-wizard-overlay';
        overlay.style.display = 'none';

        const panel = document.createElement('div');
        panel.className = 'layout-wizard-panel';
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-label', 'Layout setup');

        overlay.appendChild(panel);
        document.body.appendChild(overlay);

        this.overlay = overlay;
        this.panel = panel;
    }

    saveSnapshot() {
        this.snapshot = getConfig(this.isPortrait);
    }

    restoreSnapshot() {
        if (!this.snapshot) return Promise.resolve();
        updateConfig(this.isPortrait, this.snapshot);
        triggerResize();
        return Promise.resolve();
    }

    applyWizardDefaultsIfNeeded() {
        const c = getConfig(this.isPortrait);
        const w = window.innerWidth;
        const h = window.innerHeight;
        const qw = w / 4;
        const qh = h / 4;
        const dpadSize = c.buttonSizes.dpad;
        const abSize = c.buttonSizes.ab;
        const startSelectSize = c.buttonSizes.startSelect;

        setUseDiscreteMultipliers(this.isPortrait, false);
        if (c.gameScreenScale === 'fill' || c.gameScreenScale === null) {
            setGameScreenScale(this.isPortrait, 1);
            setFreeScaleSize(this.isPortrait, 1);
        }
        setOffsets(this.isPortrait, 'dpad', {
            left: Math.max(0, Math.round(qw - dpadSize / 2)),
            bottom: Math.max(0, Math.round(qh - dpadSize / 2)),
        });
        setOffsets(this.isPortrait, 'ab', {
            right: Math.max(0, Math.round(qw - abSize / 2)),
            bottom: Math.max(0, Math.round(qh - abSize / 2)),
            gap: 44,
            verticalAlignment: 50,
        });
        setOffsets(this.isPortrait, 'startSelect', {
            bottom: Math.max(0, Math.round(qh / 2 - startSelectSize / 2)),
            gap: 44,
        });
        triggerResize();
    }

    goNext() {
        this.saveSnapshot();
        this.stepIndex++;
        if (this.stepIndex >= STEPS.length) {
            this.hide();
            return;
        }
        this.renderStep();
    }

    goBack() {
        if (this.stepIndex === 0) {
            this.hide();
            return;
        }
        this.stepIndex--;
        this.saveSnapshot();
        this.renderStep();
    }

    renderStep() {
        const panel = this.panel;
        if (!panel) return;
        panel.innerHTML = '';
        const stepName = STEPS[this.stepIndex];
        panel.className = 'layout-wizard-panel layout-wizard-step-' + stepName;

        const isScreenStep =
            stepName === 'useSuggestedConfig' ||
            stepName === 'screenSize' ||
            stepName === 'screenPosition' ||
            stepName === 'roundedCorners';
        this.overlay.classList.remove('layout-wizard-modal-at-bottom', 'layout-wizard-modal-at-top');
        this.overlay.classList.add(isScreenStep ? 'layout-wizard-modal-at-bottom' : 'layout-wizard-modal-at-top');

        const total = STEPS.length;
        const current = this.stepIndex + 1;

        const header = document.createElement('div');
        header.className = 'layout-wizard-header';
        const progressSpan = document.createElement('span');
        progressSpan.className = 'layout-wizard-progress';
        progressSpan.textContent = `Step ${current} of ${total}`;
        header.appendChild(progressSpan);
        const exitBtn = document.createElement('button');
        exitBtn.type = 'button';
        exitBtn.className = 'layout-wizard-close';
        exitBtn.innerHTML = '×';
        exitBtn.setAttribute('aria-label', 'Exit and discard all changes');
        exitBtn.title = 'Exit and discard all changes';
        exitBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.exitAndDiscard();
        });
        header.appendChild(exitBtn);
        panel.appendChild(header);

        const title = document.createElement('h2');
        title.className = 'layout-wizard-title';
        panel.appendChild(title);

        const actions = document.createElement('div');
        actions.className = 'layout-wizard-actions';
        panel.appendChild(actions);

        const nav = document.createElement('div');
        nav.className = 'layout-wizard-nav';
        panel.appendChild(nav);

        const backBtn = document.createElement('button');
        backBtn.type = 'button';
        backBtn.className = 'layout-wizard-btn layout-wizard-btn-back';
        backBtn.textContent = 'Back';
        backBtn.addEventListener('click', () => this.goBack());
        nav.appendChild(backBtn);

        const confirmBtn = document.createElement('button');
        confirmBtn.type = 'button';
        confirmBtn.className = 'layout-wizard-btn layout-wizard-btn-confirm';
        confirmBtn.textContent = this.stepIndex === STEPS.length - 1 ? 'Done' : 'Confirm';
        confirmBtn.addEventListener('click', () => this.goNext());
        nav.appendChild(confirmBtn);

        const previewBtn = document.createElement('button');
        previewBtn.type = 'button';
        previewBtn.className = 'layout-wizard-btn layout-wizard-btn-link';
        previewBtn.textContent = 'Preview';
        previewBtn.title = 'Hide modal to glance at your layout. Tap anywhere to show it again.';
        previewBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.enterPreview();
        });
        nav.appendChild(previewBtn);

        const config = getConfig(this.isPortrait);
        const h = window.innerHeight;

        switch (stepName) {
            case 'useSuggestedConfig':
                title.textContent = 'Use a suggested layout?';
                const useSuggestedWrap = document.createElement('div');
                useSuggestedWrap.className = 'layout-wizard-button-row';
                const yesBtn = document.createElement('button');
                yesBtn.type = 'button';
                yesBtn.className = 'layout-wizard-btn layout-wizard-btn-confirm';
                yesBtn.textContent = 'Yes';
                yesBtn.addEventListener('click', async () => {
                    const applied = await fetchAndApplyMatchedConfig(this.isPortrait);
                    if (applied) triggerResize();
                    this.saveSnapshot();
                    this.stepIndex++;
                    this.renderStep();
                });
                const noBtn = document.createElement('button');
                noBtn.type = 'button';
                noBtn.className = 'layout-wizard-btn layout-wizard-btn-back';
                noBtn.textContent = 'No, use current';
                noBtn.addEventListener('click', () => {
                    this.saveSnapshot();
                    this.stepIndex++;
                    this.renderStep();
                });
                useSuggestedWrap.appendChild(yesBtn);
                useSuggestedWrap.appendChild(noBtn);
                actions.appendChild(useSuggestedWrap);
                nav.innerHTML = '';
                const backOnly = document.createElement('button');
                backOnly.type = 'button';
                backOnly.className = 'layout-wizard-btn layout-wizard-btn-back';
                backOnly.textContent = 'Back';
                backOnly.addEventListener('click', () => this.goBack());
                nav.appendChild(backOnly);
                const exitOnly = document.createElement('button');
                exitOnly.type = 'button';
                exitOnly.className = 'layout-wizard-btn layout-wizard-btn-link';
                exitOnly.textContent = 'Exit';
                exitOnly.title = 'Exit and discard all changes';
                exitOnly.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.exitAndDiscard();
                });
                nav.appendChild(exitOnly);
                break;
            case 'screenSize':
                title.textContent = 'Screen size';
                this.addSizeButtons(
                    actions,
                    0.25,
                    0.5,
                    4,
                    () => getConfig(this.isPortrait).freeScaleSize,
                    (v) => {
                        setFreeScaleSize(this.isPortrait, v);
                        setGameScreenScale(this.isPortrait, null);
                        triggerResize();
                    },
                    'Bigger',
                    'Smaller'
                );
                break;
            case 'screenPosition':
                title.textContent = 'Screen position';
                this.addMoveVerticalButtons(
                    actions,
                    8,
                    0,
                    h,
                    () => getConfig(this.isPortrait).screenTopOffset,
                    (v) => {
                        setScreenTopOffset(this.isPortrait, v);
                        triggerResize();
                    },
                    'Move Up',
                    'Move Down'
                );
                break;
            case 'roundedCorners':
                title.textContent = 'Border radius';
                const radiusStep = 4;
                const radiusMax = 80;
                const roundedWrap = document.createElement('div');
                roundedWrap.className = 'layout-wizard-button-row';
                const moreRoundBtn = document.createElement('button');
                moreRoundBtn.type = 'button';
                moreRoundBtn.className = 'layout-wizard-btn layout-wizard-btn-action';
                moreRoundBtn.textContent = 'More round';
                moreRoundBtn.addEventListener('click', () => {
                    const c = getConfig(this.isPortrait);
                    const current = c.screenBorderRadius ?? 0;
                    setScreenBorderRadius(this.isPortrait, Math.min(radiusMax, current + radiusStep));
                    triggerResize();
                });
                const moreSquareBtn = document.createElement('button');
                moreSquareBtn.type = 'button';
                moreSquareBtn.className = 'layout-wizard-btn layout-wizard-btn-action';
                moreSquareBtn.textContent = 'More square';
                moreSquareBtn.addEventListener('click', () => {
                    const c = getConfig(this.isPortrait);
                    const current = c.screenBorderRadius ?? 0;
                    setScreenBorderRadius(this.isPortrait, Math.max(0, current - radiusStep));
                    triggerResize();
                });
                roundedWrap.appendChild(moreRoundBtn);
                roundedWrap.appendChild(moreSquareBtn);
                actions.appendChild(roundedWrap);
                break;
            case 'dpadSize':
                title.textContent = 'D-Pad size';
                this.addSizeButtons(
                    actions,
                    4,
                    24,
                    120,
                    () => getConfig(this.isPortrait).buttonSizes.dpad,
                    (v) => {
                        setButtonSize(this.isPortrait, 'dpad', v);
                        triggerResize();
                    },
                    'Bigger',
                    'Smaller'
                );
                break;
            case 'dpadPosition':
                title.textContent = 'D-Pad position';
                this.addMoveFourButtons(actions, 8, 'dpad', (upd) => {
                    setOffsets(this.isPortrait, 'dpad', upd);
                    triggerResize();
                });
                break;
            case 'abSize':
                title.textContent = 'A / B button size';
                this.addSizeButtons(
                    actions,
                    4,
                    24,
                    120,
                    () => getConfig(this.isPortrait).buttonSizes.ab,
                    (v) => {
                        setButtonSize(this.isPortrait, 'ab', v);
                        setButtonFontSize(this.isPortrait, 'ab', Math.max(8, Math.round(v * 0.45)));
                        triggerResize();
                    },
                    'Bigger',
                    'Smaller'
                );
                break;
            case 'abPosition':
                title.textContent = 'A / B position';
                this.addMoveFourButtons(actions, 8, 'ab', (upd) => {
                    setOffsets(this.isPortrait, 'ab', upd);
                    triggerResize();
                });
                break;
            case 'abGapAlignment':
                title.textContent = 'A / B spacing';
                this.addSizeButtons(
                    actions,
                    4,
                    8,
                    80,
                    () => getConfig(this.isPortrait).offsets.ab.gap ?? 44,
                    (v) => {
                        setOffsets(this.isPortrait, 'ab', { gap: v });
                        triggerResize();
                    },
                    'Bigger gap',
                    'Smaller gap'
                );
                break;
            case 'abAlignment':
                title.textContent = 'A/B Alignment';
                this.addSizeButtons(
                    actions,
                    5,
                    0,
                    100,
                    () => getConfig(this.isPortrait).offsets.ab.verticalAlignment ?? 50,
                    (v) => {
                        setOffsets(this.isPortrait, 'ab', { verticalAlignment: v });
                        triggerResize();
                    },
                    'B higher',
                    'B lower',
                    true
                );
                break;
            case 'abFontSize':
                title.textContent = 'A / B font size';
                this.addSizeButtons(
                    actions,
                    2,
                    8,
                    48,
                    () => getConfig(this.isPortrait).buttonFontSizes?.ab ?? 20,
                    (v) => {
                        setButtonFontSize(this.isPortrait, 'ab', v);
                        triggerResize();
                    },
                    'Bigger',
                    'Smaller'
                );
                break;
            case 'startSelectSize':
                title.textContent = 'Start / Select size';
                this.addSizeButtons(
                    actions,
                    2,
                    12,
                    80,
                    () => getConfig(this.isPortrait).buttonSizes.startSelect,
                    (v) => {
                        setButtonSize(this.isPortrait, 'startSelect', v);
                        setButtonFontSize(
                            this.isPortrait,
                            'startSelect',
                            Math.max(8, Math.round(v * 0.8))
                        );
                        triggerResize();
                    },
                    'Bigger',
                    'Smaller'
                );
                break;
            case 'startSelectPosition':
                title.textContent = 'Start / Select position';
                this.addMoveVerticalButtons(
                    actions,
                    8,
                    0,
                    h,
                    () => getConfig(this.isPortrait).offsets.startSelect.bottom,
                    (v) => {
                        setOffsets(this.isPortrait, 'startSelect', { bottom: v });
                        triggerResize();
                    },
                    'Move Up',
                    'Move Down'
                );
                break;
            case 'startSelectGap':
                title.textContent = 'Start / Select gap';
                this.addSizeButtons(
                    actions,
                    4,
                    8,
                    120,
                    () => getConfig(this.isPortrait).offsets.startSelect.gap ?? 44,
                    (v) => {
                        setOffsets(this.isPortrait, 'startSelect', { gap: v });
                        triggerResize();
                    },
                    'Bigger gap',
                    'Smaller gap'
                );
                break;
            case 'startSelectFontSize':
                title.textContent = 'Start / Select font size';
                this.addSizeButtons(
                    actions,
                    2,
                    8,
                    48,
                    () => getConfig(this.isPortrait).buttonFontSizes?.startSelect ?? 20,
                    (v) => {
                        setButtonFontSize(this.isPortrait, 'startSelect', v);
                        triggerResize();
                    },
                    'Bigger',
                    'Smaller'
                );
                break;
            case 'final':
                title.textContent = 'All set';
                nav.innerHTML = '';
                const redo = document.createElement('button');
                redo.type = 'button';
                redo.className = 'layout-wizard-btn layout-wizard-btn-back';
                redo.textContent = 'Redo setup';
                redo.addEventListener('click', () => {
                    this.stepIndex = 0;
                    this.saveSnapshot();
                    this.applyWizardDefaultsIfNeeded();
                    this.renderStep();
                });
                nav.appendChild(redo);
                const done = document.createElement('button');
                done.type = 'button';
                done.className = 'layout-wizard-btn layout-wizard-btn-confirm';
                done.textContent = 'Keep layout';
                done.addEventListener('click', () => this.hide());
                nav.appendChild(done);
                const previewFinal = document.createElement('button');
                previewFinal.type = 'button';
                previewFinal.className = 'layout-wizard-btn layout-wizard-btn-link';
                previewFinal.textContent = 'Preview';
                previewFinal.title = 'Hide modal to glance at your layout. Tap anywhere to show it again.';
                previewFinal.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.enterPreview();
                });
                nav.appendChild(previewFinal);
                break;
        }
    }

    addSizeButtons(container, step, min, max, getValue, setValue, labelBigger, labelSmaller, noClamp) {
        const wrap = document.createElement('div');
        wrap.className = 'layout-wizard-button-row';
        const smaller = document.createElement('button');
        smaller.type = 'button';
        smaller.className = 'layout-wizard-btn layout-wizard-btn-action';
        smaller.textContent = labelSmaller;
        const bigger = document.createElement('button');
        bigger.type = 'button';
        bigger.className = 'layout-wizard-btn layout-wizard-btn-action';
        bigger.textContent = labelBigger;

        const update = () => {
            const v = getValue();
            smaller.disabled = !noClamp && v <= min;
            bigger.disabled = !noClamp && v >= max;
        };

        smaller.addEventListener('click', () => {
            const v = Math.max(min, getValue() - step);
            setValue(v);
            update();
        });
        bigger.addEventListener('click', () => {
            const v = Math.min(max, getValue() + step);
            setValue(v);
            update();
        });

        wrap.appendChild(smaller);
        wrap.appendChild(bigger);
        container.appendChild(wrap);
        update();
    }

    addMoveVerticalButtons(container, step, minVal, maxVal, getValue, onChange, labelUp, labelDown) {
        const wrap = document.createElement('div');
        wrap.className = 'layout-wizard-button-row';
        const downBtn = document.createElement('button');
        downBtn.type = 'button';
        downBtn.className = 'layout-wizard-btn layout-wizard-btn-action';
        downBtn.textContent = labelDown;
        const upBtn = document.createElement('button');
        upBtn.type = 'button';
        upBtn.className = 'layout-wizard-btn layout-wizard-btn-action';
        upBtn.textContent = labelUp;
        downBtn.addEventListener('click', () => {
            const v = Math.min(maxVal, getValue() + step);
            onChange(v);
        });
        upBtn.addEventListener('click', () => {
            const v = Math.max(minVal, getValue() - step);
            onChange(v);
        });
        wrap.appendChild(downBtn);
        wrap.appendChild(upBtn);
        container.appendChild(wrap);
    }

    addMoveFourButtons(container, step, group, onChange) {
        const wrap = document.createElement('div');
        wrap.className = 'layout-wizard-four-buttons';
        const getOffsets = () => {
            const c = getConfig(this.isPortrait).offsets;
            return group === 'dpad' ? c.dpad : c.ab;
        };
        const buttons = [
            [
                'Move Up',
                () => {
                    const o = getOffsets();
                    onChange({ bottom: Math.min(window.innerHeight, (o.bottom ?? 0) + step) });
                },
            ],
            [
                'Move Down',
                () => {
                    const o = getOffsets();
                    onChange({ bottom: Math.max(0, (o.bottom ?? 0) - step) });
                },
            ],
            [
                'Move Left',
                () => {
                    const o = getOffsets();
                    if (group === 'dpad') {
                        onChange({ left: Math.max(0, (o.left ?? 0) - step) });
                    } else {
                        onChange({ right: Math.min(window.innerWidth, (o.right ?? 0) + step) });
                    }
                },
            ],
            [
                'Move Right',
                () => {
                    const o = getOffsets();
                    if (group === 'dpad') {
                        onChange({ left: Math.min(window.innerWidth, (o.left ?? 0) + step) });
                    } else {
                        onChange({ right: Math.max(0, (o.right ?? 0) - step) });
                    }
                },
            ],
        ];
        buttons.forEach(([label, fn]) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'layout-wizard-btn layout-wizard-btn-action';
            b.textContent = label;
            b.addEventListener('click', fn);
            wrap.appendChild(b);
        });
        container.appendChild(wrap);
    }
}
